// ═══════════════════════════════════════════════════════════════════════════
//  EL AUDIO — jembatan ke ekstensi Chrome di PC room (5 Okt 2026)
//
//  Audio iframe YouTube lintas-origin: halaman ini TIDAK bisa mengolahnya.
//  Yang bisa: ekstensi Chrome "EL Audio" (karaoke-app/ekstensi-chrome) yang
//  menangkap suara tab TV (tabCapture), memutarnya kembali lewat pengurang
//  vokal + geser nada. Halaman cuma memberi tahu APA yang diinginkan.
//
//  Tanpa ekstensi (APK TV box Android, browser lain, PC yang belum dipasang)
//  tidak ada satu pesan pun yang datang → `ada` tetap false → pemutar
//  berperilaku persis seperti sebelumnya.
//
//  Protokol (window.postMessage, origin halaman sendiri):
//    halaman → ekstensi : {sumber:'el-karaoke', jenis:'halo'|'mulai'|'atur', …}
//    ekstensi → halaman : {sumber:'el-audio', jenis:'siap'|'status', …}
//  Lihat ekstensi-chrome/jembatan.js.
//
//  Sengaja TIDAK memakai getDisplayMedia di halaman: suara olahan yang
//  diputar halaman ikut tertangkap lagi → umpan balik (loop).
// ═══════════════════════════════════════════════════════════════════════════

import { useSyncExternalStore } from 'react'

export type StatusElAudio = 'belum' | 'menyiapkan' | 'aktif' | 'galat'

export type KeadaanElAudio = {
  /** ekstensi terpasang & menjawab di tab ini */
  ada: boolean
  versi: string
  status: StatusElAudio
  pesan: string
  /** worklet geser nada termuat di ekstensi */
  nadaBisa: boolean
}

const STATUS_SAH: StatusElAudio[] = ['belum', 'menyiapkan', 'aktif', 'galat']

let keadaan: KeadaanElAudio = { ada: false, versi: '', status: 'belum', pesan: '', nadaBisa: false }
const pendengar = new Set<() => void>()
let terpasang = false

function ubah(isi: Partial<KeadaanElAudio>) {
  const baru = { ...keadaan, ...isi }
  if (baru.ada === keadaan.ada && baru.versi === keadaan.versi && baru.status === keadaan.status
    && baru.pesan === keadaan.pesan && baru.nadaBisa === keadaan.nadaBisa) return
  keadaan = baru
  pendengar.forEach(f => f())
}

function kirim(isi: Record<string, unknown>) {
  try { window.postMessage({ sumber: 'el-karaoke', ...isi }, location.origin) } catch { /* abaikan */ }
}

/** Pasang pendengar + sapa ekstensi. Aman dipanggil berkali-kali. */
export function pasangElAudio() {
  if (terpasang || typeof window === 'undefined') return
  terpasang = true
  window.addEventListener('message', (e: MessageEvent) => {
    // Iframe YouTube juga mengirim postMessage — hanya pesan dari jendela ini
    // sendiri, origin sendiri, dan berbentuk benar yang diterima.
    if (e.source !== window || e.origin !== location.origin) return
    const d = e.data as Record<string, unknown> | null
    if (!d || typeof d !== 'object' || d.sumber !== 'el-audio') return
    const versi = typeof d.versi === 'string' ? d.versi.slice(0, 20) : keadaan.versi
    if (d.jenis === 'siap') {
      ubah({ ada: true, versi })
    } else if (d.jenis === 'status') {
      const st = STATUS_SAH.includes(d.status as StatusElAudio) ? (d.status as StatusElAudio) : 'belum'
      ubah({
        ada: true, versi, status: st,
        pesan: typeof d.pesan === 'string' ? d.pesan.slice(0, 200) : '',
        nadaBisa: d.nadaBisa === true,
      })
    }
  })
  kirim({ jenis: 'halo' })
}

function langgan(f: () => void) {
  pasangElAudio()
  pendengar.add(f)
  return () => { pendengar.delete(f) }
}

export function useElAudio(): KeadaanElAudio {
  return useSyncExternalStore(langgan, () => keadaan, () => keadaan)
}

/** Efek yang diinginkan. youtube:false = netral (suara lewat apa adanya). */
export function aturElAudio(a: { youtube: boolean; vokal: boolean; nada: number }) {
  if (!keadaan.ada) return
  const nada = Math.max(-6, Math.min(6, Math.round(a.nada || 0)))
  kirim({ jenis: 'atur', youtube: !!a.youtube, vokal: !!a.vokal, nada })
}

/** Coba mulai capture. Chrome biasanya menolak tanpa klik ikon — tidak apa. */
export function mintaMulaiElAudio() {
  if (keadaan.ada) kirim({ jenis: 'mulai' })
}
